'use client'

import React from 'react'
import type { Media as MediaType } from '@/payload-types'
import { Media } from '@/components/Media'
import { cn } from '@/utilities/ui'
import { getMediaUrl } from '@/utilities/getMediaUrl'

type MediaMode = 'image' | 'backgroundImage'
type MediaBackground = 'beige' | 'black' | 'white'
type ImagePosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

interface MediaContentProps {
  mediaType?: MediaMode
  media?: MediaType | null
  backgroundImage?: MediaType | null
  mediaBackground?: MediaBackground
  imagePosition?: ImagePosition
  className?: string
}

const backgroundColors: Record<MediaBackground, string> = {
  beige: 'bg-civo-cloud',
  black: 'bg-civo-black',
  white: 'bg-white',
}

const absolutePositionClasses: Record<ImagePosition, string> = {
  'top-left': 'top-0 left-0',
  'top-right': 'top-0 right-0',
  'bottom-left': 'bottom-0 left-0',
  'bottom-right': 'bottom-0 right-0',
}

export const MediaContent: React.FC<MediaContentProps> = ({
  mediaType = 'image',
  media,
  backgroundImage,
  mediaBackground = 'beige',
  imagePosition = 'bottom-right',
  className,
}) => {
  // Background Image mode - just a full cover background image
  if (mediaType === 'backgroundImage' && backgroundImage) {
    const backgroundImageUrl = getMediaUrl(backgroundImage.url, backgroundImage.updatedAt)
    return (
      <div
        className={cn(
          'rounded-[16px] w-full h-full overflow-hidden',
          'min-h-80 lg:min-h-[400px] xl:min-h-[504px]',
          className,
        )}
        style={{
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
        role="img"
        aria-label={backgroundImage.alt || ''}
      />
    )
  }

  // Image mode - background color with positioned image
  return (
    <div
      className={cn(
        'relative rounded-[16px] w-full h-full overflow-hidden',
        'min-h-80 lg:min-h-[400px] xl:min-h-[504px]',
        backgroundColors[mediaBackground],
        className,
      )}
    >
      {media && (
        <div className={cn('absolute', absolutePositionClasses[imagePosition])}>
          <Media
            resource={media}
            imgClassName="h-full w-auto object-contain"
            disablePlaceholder
          />
        </div>
      )}
    </div>
  )
}